import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Button, EMPHASIS, Heading, Label, Text, Toggle} from '@forecast-it/design-system';
import {ExportDefaultToggle} from './exportDefaults';

const CardWrapper = styled.section`
	margin-top: ${props => props.theme.sys.spacing['600']};
	padding: ${props => props.theme.sys.spacing['600']};
	border: ${props => props.theme.sys.size.borderwidth.thin} solid ${props => props.theme.sys.color.border.decorative};
	border-radius: ${props => props.theme.sys.size.borderradius['2']};
`;

const Header = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${props => props.theme.sys.spacing['200']};
`;

const Row = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: ${props => props.theme.sys.spacing['600']};
	max-width: 720px;
	margin-top: ${props => props.theme.sys.spacing['400']};
`;

const RowText = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: ${props => props.theme.sys.spacing['100']};
`;

const BeforeToggles = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${props => props.theme.sys.spacing['200']};
	max-width: 720px;
	margin-top: ${props => props.theme.sys.spacing['400']};
`;

const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: ${props => props.theme.sys.spacing['600']};
`;

type Props<F extends string> = {
	titleId: string;
	introId: string;
	saveId: string;
	toggles: ReadonlyArray<ExportDefaultToggle<F>>;
	values: Record<F, boolean>;
	onToggle: (field: F, checked: boolean) => void;
	onSave: () => void;
	saving: boolean;
	cyPrefix: string;
	beforeToggles?: React.ReactNode;
};

export const ExportDefaultsCard = <F extends string>({
	titleId,
	introId,
	saveId,
	toggles,
	values,
	onToggle,
	onSave,
	saving,
	cyPrefix,
	beforeToggles,
}: Props<F>) => {
	const {formatMessage} = useIntl();
	return (
		<CardWrapper data-cy={`${cyPrefix}-section`}>
			<Header>
				<Heading size="3" as="h2">
					{formatMessage({id: titleId})}
				</Heading>
				<Text>{formatMessage({id: introId})}</Text>
			</Header>
			{beforeToggles ? <BeforeToggles data-cy={`${cyPrefix}-before-toggles`}>{beforeToggles}</BeforeToggles> : null}
			{toggles.map(({field, labelId, helperId}) => (
				<Row key={field}>
					<RowText>
						<Label>{formatMessage({id: labelId})}</Label>
						<Text color="auxiliary">{formatMessage({id: helperId})}</Text>
					</RowText>
					<Toggle
						checked={values[field]}
						onChange={checked => onToggle(field, checked)}
						disabled={saving}
						cy={`${cyPrefix}-toggle-${field}`}
					/>
				</Row>
			))}
			<Footer>
				<Button emphasis={EMPHASIS.HIGH} onPress={onSave} waiting={saving} data-cy={`${cyPrefix}-save`}>
					{formatMessage({id: saveId})}
				</Button>
			</Footer>
		</CardWrapper>
	);
};
